import { useEffect, useState } from 'react';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import usePersistedKeyValueStore from '../../../hooks/usePersistedKeyValueStore';

export type UseSearchTextArgs = {
    onClose: () => void;
    medicalProfileId: string;
};

export type UseSearchText = {
    nonDebouncedSearchText: string;
    nonDebouncedOnChange: (value: string) => void;
    debouncedSearchText: string;
    onCloseAndClearSearchText: () => void;
    onCloseAndKeepSearchText: () => void;
    isLoadingSearchText: boolean;
};

export default function useSearchText({ onClose, medicalProfileId }: UseSearchTextArgs): UseSearchText {
    // persisted search text.  Do not use this as the source of truth.  Small timing delays can cause the search text to be out of sync with the input
    const persistedSearchStore = usePersistedKeyValueStore<string>({
        category: 'search',
        key: `medicalProfileId:${medicalProfileId}`,
        defaultValue: '',
        expiration: 1000 * 60 * 10, // 10 minutes
    });

    // local input search text.  This is a debounced value.  The immediate value is from the useDebouncedValue hook
    const [inputSearchText, setInputSearchText] = useState<string>('');

    // update the persisted search text when the input changes
    useEffect(() => {
        persistedSearchStore.setValue(inputSearchText);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputSearchText]);

    // debounced search text so search requests are not made too frequently
    const debouncedSearchText = useDebouncedValue<string>({
        debouncedValue: inputSearchText,
        debouncedOnChange: setInputSearchText,
        debounceInterval: 800,
    });

    // default to persisted search text when the component mounts and the store is not loading
    useEffect(() => {
        if (!persistedSearchStore.isLoading) {
            debouncedSearchText.onChange(persistedSearchStore.value, true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [persistedSearchStore.isLoading]);

    const onCloseAndClearSearchText = () => {
        onClose();
        persistedSearchStore.setValue('');
    };

    const onCloseAndKeepSearchText = () => {
        onClose();
    };

    return {
        nonDebouncedSearchText: debouncedSearchText.value,
        nonDebouncedOnChange: debouncedSearchText.onChange,
        debouncedSearchText: inputSearchText,
        isLoadingSearchText: persistedSearchStore.isLoading,
        onCloseAndClearSearchText,
        onCloseAndKeepSearchText,
    };
}
